import { notifications } from '@mantine/notifications'
import { useCallback, useMemo } from 'react'
import { useRawOrdersQuery } from '@/entities/order/model'
import { DELIVERING_TO_CUSTOMER_STATUS } from './order.zebraScan'
import { useGroupScanState } from './useGroupScanState'
import { useOrderGroupMembers } from './useOrderGroupMembers'

/** Transitions out of Arrived that a grouped order must not take while crates are outstanding. */
const GROUP_GATED_ACTIONS = new Set([DELIVERING_TO_CUSTOMER_STATUS, 'Completed'])

/**
 * Whether an action is one the group gate applies to at all.
 *
 * Only forward transitions out of Arrived are gated. Everything else — notably
 * OnTheWay -> Arrived, which is how a group reaches the point where scanning is
 * possible — must stay open, or the group is trapped short of its scan window.
 *
 * Exported so callers that already hold the group's orders (the group card, via
 * useGroupScanState) apply the identical rule without re-resolving membership.
 */
export function isGroupGatedAction(action: string): boolean {
  return GROUP_GATED_ACTIONS.has(action)
}

/**
 * A blocked transition is silent otherwise: the control simply does not respond, which reads as a
 * broken button. This is the more so while BD-8185 is outstanding, since a fully scanned member
 * still shows its own "We're on it" CTA without knowing its partner has crates left.
 */
export function notifyGroupScanIncomplete(): void {
  notifications.show({
    color: 'red',
    title: 'Group not fully scanned',
    message: 'Every order in this group must be scanned before it can move on.',
    autoClose: 5000,
  })
}

/**
 * BD-8186: a grouped order stays in Arrived until every order in it is fully scanned.
 *
 * The requirement is about the orders, not about one button, so every route to the same
 * outcome has to consult this: the group card's action, the order-details primary action,
 * and the status drawer / popover, which each drive `useOrderStatusMutation` themselves.
 *
 * Non-grouped orders and groups outside the scan window are never blocked.
 */
export function useGroupTransitionGuard(orderId: string | undefined) {
  const { data: allOrders } = useRawOrdersQuery()
  const groupMemberOrderIds = useOrderGroupMembers(orderId ?? '')

  const groupMemberOrders = useMemo(
    () => (groupMemberOrderIds.length > 1
      ? (allOrders ?? []).filter(order => groupMemberOrderIds.includes(order.orderId))
      : []),
    [allOrders, groupMemberOrderIds],
  )

  const scanState = useGroupScanState(groupMemberOrders)
  const isBlockedByGroupScan = groupMemberOrders.length > 1 && !scanState.canTransition

  const isTransitionBlocked = useCallback(
    (action: string) => isBlockedByGroupScan && isGroupGatedAction(action),
    [isBlockedByGroupScan],
  )

  return { isBlockedByGroupScan, isTransitionBlocked }
}
